import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { InfoDialogComponent } from './components/info-dialog/info-dialog.component';
import {
  InputDialogComponent,
  InputType,
} from './components/input-dialog/input-dialog.component';
import { YesNoDialogComponent } from './components/yes-no-dialog/yes-no-dialog.component';
import { SelectDialogComponent } from './components/select-dialog/select-dialog.component';
import { CameraDialogComponent } from './components/camera-dialog/camera-dialog.component';
import { first } from 'rxjs';
import { OpeningTimesDialogComponent } from './components/opening-times/opening-times-dialog/opening-times-dialog.component';
import { OpeningTimesDialogInput } from './components/opening-times/current-opening-times.service';

export interface OpenInputDialogOptions {
  inputCheckerFn: (input: string) => boolean;
  afterCloseFn: (input: string) => void | Promise<void>;
  inputLabel: string;
  text: string;
  errorMessage: string;
  defaultInput?: string;
  type?: InputType;
  formatterFn?: (value: string) => string;
}

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  readonly dialog = inject(MatDialog);

  openPickOpeningTimesDialog(openingTimesDialogInput: OpeningTimesDialogInput) {
    this.dialog.open(OpeningTimesDialogComponent, {
      data: openingTimesDialogInput,
    });
  }

  openCameraDialog(
    getImagesFn: () => string[],
    addImageFn: (image: string) => void,
    afterCloseFn: () => void,
  ) {
    this.dialog.open(CameraDialogComponent, {
      data: { getImages: getImagesFn, addImage: addImageFn },
    });
    this.dialog.afterAllClosed.pipe(first()).subscribe(() => afterCloseFn());
  }

  openSelectDialog(
    options: { option: string; label: string }[],
    label: string,
    text: string,
    afterOptionSelectedFn: (option: string) => void,
    headline?: string,
    selectedValue?: string,
  ) {
    this.dialog.open(SelectDialogComponent, {
      data: {
        options: options,
        label: label,
        text: text,
        afterOptionSelected: afterOptionSelectedFn,
        headline: headline,
        selectedValue: selectedValue,
      },
    });
  }

  openInfoDialog = (info: string) => {
    this.dialog.open(InfoDialogComponent, { data: { info: info } });
  };

  openInputDialog(options: OpenInputDialogOptions) {
    const {
      inputCheckerFn,
      afterCloseFn,
      inputLabel,
      text,
      errorMessage,
      defaultInput = '',
      type = 'text',
      formatterFn,
    } = options;
    const inputDialog = this.dialog.open(InputDialogComponent, {
      data: {
        tryUpdate: (input: string) => {
          if (inputCheckerFn(input)) {
            afterCloseFn(input);
            inputDialog.close();
          }
        },
        inputLabel: inputLabel,
        text: text,
        inputChecker: inputCheckerFn,
        errorMessage: errorMessage,
        defaultInput: defaultInput,
        type: type,
        formatter: formatterFn,
      },
    });
    inputDialog.disableClose = true;
  }

  openYesNoDialog(
    text: string,
    yesButtonText: string,
    noButtonText: string,
    yesFn: () => void,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    noFn: () => void = () => { },
  ) {
    this.dialog.open(YesNoDialogComponent, {
      data: {
        text: text,
        yesButtonText: yesButtonText,
        noButtonText: noButtonText,
        yes: yesFn,
        no: noFn,
      },
    });
  }
}
